import { useEffect, useMemo, useRef, useState } from "react";
import i18n from 'i18n';
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { getFileExtension, useFederatedUploadFilter } from "Utils/ViewerUtils";
import { ObraActions } from "Store/Obra/Obra.actions";
import { IAutodeskViewer } from "Data/interfaces/Viewer/IAutodeskViewer";
import { FederatedViewerActions } from "Store/FederatedViewer/FederatedViewer.actions";
import toastHandler from 'Utils/toastHandler';
import { IUserInforResponse } from "Data/interfaces/Auth/IUserInfoResponse";
import { IDisciplineViewerList } from "Data/interfaces/FederatedViewer/IDisciplineViewerList";
import { Mixpanel } from "Utils/MixPanel";
import { getCurrentTenant, getTenants } from "Store/Tenant/Tenant.selector";
import { useFeatureFlag } from "Hooks/useFeatureFlag";
import { useTimeout } from "Context/TimeoutContext";
import { ViewerApi } from "Data/Viewer.api";
import { ViewerEngineEnum } from "Data/enums/ViewerEngine";
import { BucketFileStatusEnum } from "Data/interfaces/ListagemArquivos/IListFilesResponse";
import { COORDLY_3D_VIEWER_EXTENSION } from "Utils/ViewerUtils";
import { autodeskStatusOfFile, isConvertingFile, isNativeReadyFile } from "./federationUrn";

export interface INode {
  node: number | null;
  urn: string | null;
}

export interface IView {
  view: Autodesk.Viewing.BubbleNode | null;
  urn: string | null;
  id: number | null;
}

export interface ICamera {
  camera: Autodesk.Viewing.BubbleNode | null;
  urn: string | null;
  id: number | null;
}

export interface IPrancha {
  prancha: Autodesk.Viewing.BubbleNode | null;
  urn: string | null;
  id: number | null;
}

export interface IChildrenNames {
  urn: string;
  name: string;
}

export type IDrawerOptions = 'models' |
  'views' |
  'viewpoints' |
  'pranchas' |
  'activities';

interface IUseFederatedViewer {
  isLoading: boolean;
  urns: string[];
  userInfo?: IUserInforResponse;
  viewerListDisciplines: IDisciplineViewerList[];
  loadingDiscipline: boolean;
  constructionSiteId: number;
  blockAutoRender?: boolean;
  /** Pedir a conversão nativa: só com o nosso motor utilizável (flag + WebGPU). */
  nativeAutoRender?: boolean;
}

const useFederatedViewer = ({
  isLoading,
  urns,
  userInfo,
  viewerListDisciplines,
  loadingDiscipline,
  constructionSiteId,
  blockAutoRender,
  nativeAutoRender,
}: IUseFederatedViewer) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { set, clear } = useTimeout();
  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);

  const { isUploadableExtension, onlyIfc } = useFederatedUploadFilter();
  const demoVideoViewerFlag = useFeatureFlag('demo-video-viewer');
  const demoVideoViewer = (demoVideoViewerFlag.enabled &&
    typeof demoVideoViewerFlag?.value === 'string' &&
    !['control', 'test'].includes(demoVideoViewerFlag.value))
    ? demoVideoViewerFlag.value
    : (process.env.REACT_APP_DEMO_VIDEO_VIEWER || '');

  const oneTryRender = useRef(0);
  const [drawerFederatedVisible, setDrawerFederatedVisible] = useState(true);
  const [searchDiscipline, setSearchDiscipline] = useState('');
  const [searchDisciplineFiles, setSearchDisciplineFiles] = useState('');
  const [searchItens, setSearchItens] = useState('');
  const [activeDrawer, setActiveDrawer] = useState<IDrawerOptions>('models');
  const [activeDisciplineId, setActiveDisciplineId] = useState<number | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [upfileViewer, setUpfileViewer] = useState({} as File);
  const [showSelectUploadPath, setShowSelectUploadPath] = useState(false);
  const [showCTAViewerUpgrade, setShowCTAViewerUpgrade] = useState(false);
  const [showCTAMultipleModelsUpgrade, setShowCTAMultipleModelsUpgrade] = useState(false);
  const [indexList, setIndexList] = useState(0);
  const [selectUploadPathFromMaleta, setSelectUploadPathFromMaleta] = useState(false);
  const [loadUrn, setLoadUrn] = useState<string | null>(null);
  const [toggleHideShowNode, setToggleHideShowNode] = useState<INode>({ node: null, urn: null });
  const [toggleSelectNode, setToggleSelectNode] = useState<INode>({ node: null, urn: null });
  const [showModalProgressLimitRender, setShowModalProgressLimitRender] = useState(false);
  const [toggleView, setToggleView] = useState<IView>({ view: null, urn: null, id: null });
  const [toggleViewCamera, setToggleViewCamera] = useState<ICamera>({ camera: null, urn: null, id: null });
  const [togglePrancha, setTogglePrancha] = useState<IPrancha>({ prancha: null, urn: null, id: null });
  const [mapTimeoutIds, setMapTimeoutIds] = useState<string[]>([]);

  const filteredEmptyDisciplines = useMemo(() => {
    return viewerListDisciplines.filter(discipline =>
      discipline.FilesList?.length && (
        (
          discipline.CustomName &&
          discipline.CustomName.toLowerCase().includes(searchDiscipline.toLowerCase())
        ) ||
        discipline.Discipline.Name.toLowerCase().includes(searchDiscipline.toLowerCase())
      ));
  }, [viewerListDisciplines, searchDiscipline]);

  const childrenActiveDisciplines = useMemo(() => {
    const activeDiscipline = viewerListDisciplines.find(discipline => discipline.ConstructionSiteDisciplinesId === activeDisciplineId);
    if (activeDiscipline) {
      const listFiles = activeDiscipline.FilesList;
      if (Array.isArray(listFiles)) {
        return listFiles.filter(file => file.Name.toLowerCase().includes(searchDisciplineFiles.toLowerCase()));
      }
    }
    return undefined;
  }, [activeDisciplineId, searchDisciplineFiles, viewerListDisciplines]);

  const childrenActiveUrnsNames = useMemo(() => {
    const childrenNames: IChildrenNames[] = [];
    childrenActiveDisciplines?.map(item => {
      if (item.FileInfo.BucketFile?.Urn) {
        childrenNames.push({
          urn: `urn:${item.FileInfo.BucketFile.Urn}`,
          name: item.Name,
        });
      }
    });
    return childrenNames;
  }, [viewerListDisciplines, childrenActiveDisciplines, urns]);

  useEffect(() => {
    dispatch(ObraActions.toggleCollapse(true));
    dispatch(FederatedViewerActions.listDisciplines(constructionSiteId));

    return () => {
      mapTimeoutIds.forEach(id => clear(id));
      clear('useFederatedViewer-oneTryRenderOut');
    }
  }, []);

  useEffect(() => {
    if (viewerListDisciplines.length && !loadingDiscipline) {
      viewerListDisciplines.map(discipline => {
        return dispatch(FederatedViewerActions.listFiles(discipline.ConstructionSiteDisciplinesId));
      })
    }
  }, [loadingDiscipline]);

  // RENDER NATIVO AUTOMÁTICO (flag do viewer 3D ligada): todo IFC da lista sem
  // render nativo pronto é pedido ao abrir a página e quando entra nela (upload
  // ou Coordly). Assim o switch já encontra o modelo pronto, em vez de esperar
  // a conversão do zero no clique. Um pedido por arquivo nesta sessão; o backend
  // já deduplica job ativo do mesmo arquivo.
  const nativeRequested = useRef(new Set<string>());
  useEffect(() => {
    // Sem WebGPU o arquivo convertido aqui não abriria em lugar nenhum: o nosso
    // viewer não roda e o Autodesk não lê a urn `native:`.
    if (!blockAutoRender || !nativeAutoRender || isLoading) { return; }

    viewerListDisciplines.forEach((discipline) => {
      discipline.FilesList?.forEach((file) => {
        if (file.Extension?.toLowerCase() !== COORDLY_3D_VIEWER_EXTENSION) { return; }
        const fileApiId = file.Identifier.ApiId || file.Identifier.ItemId;
        if (!fileApiId || nativeRequested.current.has(fileApiId)) { return; }
        // Pronto no nativo, ou já em conversão em qualquer motor (o hub acompanha):
        // nada a pedir.
        if (isNativeReadyFile(file) || isConvertingFile(file)) {
          return;
        }

        nativeRequested.current.add(fileApiId);
        ViewerApi.renderViewerFile({
          constructionSiteId,
          fileApiId,
          folderId: '',
          engine: ViewerEngineEnum.Native,
        })
          // Falha aqui só deixa o arquivo sem render: o switch segue desabilitado.
          .catch(() => undefined);
      });
    });
  }, [blockAutoRender, nativeAutoRender, isLoading, viewerListDisciplines, constructionSiteId]);

  // RENDERIZAÇÃO AUTOMÁTICA
  useEffect(() => {
    // Conversor automático PAUSADO até segunda ordem enquanto a flag do viewer 3D
    // estiver ativa (decisão do PO, 2026-07-27): abrir a página não enfileira mais
    // nada na Autodesk. O job segue disponível sob demanda (toggle / fallback).
    if (blockAutoRender) { return; }
    if (viewerListDisciplines.length && !isLoading && (oneTryRender.current === 0)) {
      viewerListDisciplines.map((discipline, index) => {
        // Status da conversão da Autodesk: IFC só convertido no nativo conta como
        // não convertido para ela.
        if (discipline.FilesList?.some(file => !file.FileInfo.BucketFile || file.FileInfo.BucketFile === null || (autodeskStatusOfFile(file) && autodeskStatusOfFile(file) !== BucketFileStatusEnum.Rendered))) {
          discipline.FilesList.map(file => {
            const autodeskStatus = autodeskStatusOfFile(file);
            if (file.FileInfo.BucketFile && autodeskStatus === BucketFileStatusEnum.Rendering) {
              dispatch(FederatedViewerActions.verifyViewerAutodeskProgress([{
                constructionSiteId: constructionSiteId,
                folderId: file.FileInfo.ApiFolderId || '',
                fileApiId: file.Identifier.ApiId || file.Identifier.ItemId
              }]));
            }
            // Uma conversão por vez: com a nativa andando, a da Autodesk não sai.
            if (file.FileInfo.BucketFile?.NativeStatus === BucketFileStatusEnum.Rendering) { return file; }
            if (!file.FileInfo.BucketFile || autodeskStatus === null || autodeskStatus === BucketFileStatusEnum.NotRendered) {
              const request: IAutodeskViewer = {
                constructionSiteId: constructionSiteId,
                folderId: file.FileInfo.ApiFolderId || '',
                fileApiId: file.Identifier.ApiId || file.Identifier.ItemId,
                extension: file.Extension,
                onLimitedResources: () => setShowCTAViewerUpgrade(true),
              };

              dispatch(FederatedViewerActions.setFileStatusRendering(request));
              dispatch(FederatedViewerActions.autoDeskViewer(request));

              Mixpanel.track({
                name: 'JOB_RENDER_FILE',
                props: {
                  constructionSiteId: constructionSiteId,
                  api: file.FileInfo.Api,
                  disciplineId: discipline.ConstructionSiteDisciplinesId,
                  fileId: file.Identifier.ApiId || file.Identifier.ItemId,
                  disciplineName: discipline?.CustomName || discipline?.Discipline?.Name,
                  extension: file.Extension,
                  federatedViewer: true,
                  engine: 'autodesk',
                },
                userInfo,
                currentListTenant: currentTenant,
                tenantIds: tenants && tenants.length > 0
                  ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
                  : undefined,
              });
            }
            return file;
          });
          const timeoutId = `useFederatedViewer-oneTryRender${index}`;
          clear(timeoutId);
          setMapTimeoutIds(prev => {
            if (!prev.includes(timeoutId)) {
              return [...prev, timeoutId];
            }
            return prev;
          });
          set(timeoutId, () => {
            oneTryRender.current = 1;
          }, 1000);
        }
        return discipline;
      });
      clear('useFederatedViewer-oneTryRenderOut');
      set('useFederatedViewer-oneTryRenderOut', () => {
        oneTryRender.current = 0;
      }, 5000);
    }
  }, [viewerListDisciplines, isLoading]);

  const handleUpgradePlan = () => {
    Mixpanel.track({
      name: 'CALL_T0_ACTION',
      props: {
        origin: 'federated-viewer',
        originPath: window.location.pathname
      },
      userInfo,
      currentListTenant: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
        : undefined,
    });

    if (userInfo?.CurrentRoleFk === 1) {
      navigate('/faturamento/planos');
    } else {
      window.open('https://bim.maletadoengenheiro.com.br/seja-premium');
    }
  }

  const handleShowCTARender = () => {
    setShowCTAViewerUpgrade(true);
  }

  const handleOpenCTAModal = (index: number) => {
    setShowCTAMultipleModelsUpgrade(true);
    setIndexList(index);
  }

  const handleCloseCTAModal = () => {
    setShowCTAMultipleModelsUpgrade(false);
  }

  const handleShowProgressLimitRender = () => {
    setShowModalProgressLimitRender(true);
  }

  const handleCloseProgressLimitRender = () => {
    setShowModalProgressLimitRender(false);
  }

  const handleSearch = (value: string) => {
    if (activeDrawer === 'models') {
      if (activeDisciplineId) {
        setSearchDiscipline('');
        setSearchDisciplineFiles(value);
      } else {
        setSearchDiscipline(value);
        setSearchDisciplineFiles('');
      }
    } else {
      setSearchItens(value);
      setSearchDiscipline('');
      setSearchDisciplineFiles('');
    }
  }

  const clearAllSearch = () => {
    setSearchDiscipline('');
    setSearchDisciplineFiles('');
    setSearchItens('');
  }

  const uploadFileViewer = () => {
    document.getElementById('uploadFileViewer')?.click();
  }

  const handleUploadFileViewer = (files: FileList | null) => {
    if (files) {
      if (files[0].size) {
        const extension = getFileExtension(files[0].name);
        if (isUploadableExtension(extension)) {
          setUpfileViewer(files[0]);
          setSelectUploadPathFromMaleta(false);
          setShowSelectUploadPath(true);
        } else {
          toastHandler.showError(i18n.t(onlyIfc
            ? 'messages:toasts.uploadOnlyIfc'
            : 'messages:toasts.uploadValidExtension'));
        }
      }
    }
  }

  const handleMaletaFileViewer = () => {
    setSelectUploadPathFromMaleta(true);
    setShowSelectUploadPath(true);
  }

  const handleActiveUrns = (urn: string, extension: string) => {
    dispatch(FederatedViewerActions.setUrns(urn));
    dispatch(FederatedViewerActions.setExtensionValidated({ urn, extension }));
  }

  const cancelUploadFileViewer = () => {
    setUpfileViewer({} as File);
    setShowSelectUploadPath(false);
    setSelectUploadPathFromMaleta(false);
  }

  const handleActiveList = (value: IDrawerOptions) => {
    setActiveDrawer(value);
    clearAllSearch();
  }

  const openDiscipline = (disciplineId: number) => {
    setActiveDisciplineId(disciplineId);
    clearAllSearch();
  }

  const backDisciplines = () => {
    // Keep viewer data when user goes back so the loaded viewpoint stays visible.
    setActiveDisciplineId(null);
    clearAllSearch();
  }

  const showDrawerFederated = () => {
    setDrawerFederatedVisible(true);
  };

  const closeDrawerFederated = () => {
    setDrawerFederatedVisible(false);
  };

  const handleToogleNode = (node: number | null, urn: string | null) => {
    setToggleHideShowNode({ node: null, urn: null });
    setToggleHideShowNode({ node, urn });
  };

  const handleToogleSelectNode = (node: number | null, urn: string | null) => {
    setToggleSelectNode({ node: null, urn: null });
    setToggleSelectNode({ node, urn });
  };

  const handleToogleView = (
    view: Autodesk.Viewing.BubbleNode | null,
    urn: string | null,
    id: number | null,
  ) => {
    setToggleView({ view: null, urn: null, id: null });
    setToggleView({ view, urn, id });
  };

  const handleToogleViewCamera = (
    camera: Autodesk.Viewing.BubbleNode | null,
    urn: string | null,
    id: number | null,
  ) => {
    setToggleViewCamera({ camera: null, urn: null, id: null });
    setToggleViewCamera({ camera, urn, id });
  };

  const handleTooglePrancha = (
    prancha: Autodesk.Viewing.BubbleNode | null,
    urn: string | null,
    id: number | null,
  ) => {
    setTogglePrancha({ prancha: null, urn: null, id: null });
    setTogglePrancha({ prancha, urn, id });
  };

  const handleClearAllToogles = () => {
    setLoadUrn(null);
    setToggleHideShowNode({ node: null, urn: null });
    setToggleSelectNode({ node: null, urn: null });
    setToggleViewCamera({ camera: null, urn: null, id: null });
    setTogglePrancha({ prancha: null, urn: null, id: null });
    setToggleView({ view: null, urn: null, id: null })
  };

  const handleOpenVideoModal = () => {
    setShowVideo(true);
  }

  const handleCloseVideoModal = () => {
    setShowVideo(false);
  }

  const handleLoadUrn = (urn: string | null) => {
    setLoadUrn(urn);
  }

  return {
    demoVideoViewer,
    activeDrawer,
    drawerFederatedVisible,
    activeDisciplineId,
    filteredEmptyDisciplines,
    childrenActiveDisciplines,
    searchDiscipline,
    searchDisciplineFiles,
    childrenActiveUrnsNames,
    loadUrn,
    searchItens,
    toggleView,
    toggleViewCamera,
    toggleHideShowNode,
    toggleSelectNode,
    togglePrancha,
    showVideo,
    showSelectUploadPath,
    upfileViewer,
    selectUploadPathFromMaleta,
    showCTAViewerUpgrade,
    showCTAMultipleModelsUpgrade,
    indexList,
    showModalProgressLimitRender,
    handleActiveList,
    closeDrawerFederated,
    showDrawerFederated,
    handleSearch,
    uploadFileViewer,
    handleMaletaFileViewer,
    handleShowCTARender,
    handleShowProgressLimitRender,
    openDiscipline,
    backDisciplines,
    handleActiveUrns,
    handleOpenCTAModal,
    handleToogleNode,
    handleToogleSelectNode,
    dispatch,
    handleOpenVideoModal,
    handleCloseVideoModal,
    handleUpgradePlan,
    handleLoadUrn,
    handleToogleView,
    handleToogleViewCamera,
    handleTooglePrancha,
    handleUploadFileViewer,
    cancelUploadFileViewer,
    setShowCTAViewerUpgrade,
    handleCloseCTAModal,
    handleCloseProgressLimitRender,
    handleClearAllToogles
  }
}

export default useFederatedViewer;
